import { useMemo } from 'react';
import { LuLockOpen } from 'react-icons/lu';

import type { PatternDesignOptimisation } from '~api/services';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~components/ui/select';

type OptimiserSelectorProps = {
  /** Currently selected optimisation ID */
  selectedOptimisationId: number;
  /** List of available optimisations */
  options: PatternDesignOptimisation[];
  /** Callback when a different optimisation is selected */
  onChange: (optimisationId: number) => void;
};

/**
 * Dropdown selector for choosing different optimisations within a blast
 */
const OptimiserSelector = ({ selectedOptimisationId, options, onChange }: OptimiserSelectorProps) => {
  const sortedOptions = useMemo(() => {
    // sort so the 'constrained' optimisations appear first, sorted by efficiency descending
    return options.toSorted((a, b) => {
      if (a.constrained && !b.constrained) return -1;
      if (!a.constrained && b.constrained) return 1;
      return b.efficiency - a.efficiency;
    });
  }, [options]);

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-foreground text-sm uppercase">Active scenario</h3>
      <Select value={String(selectedOptimisationId)} onValueChange={(value) => onChange(Number(value))}>
        <SelectTrigger className="w-full text-sm">
          <SelectValue placeholder="Select Optimisation" />
        </SelectTrigger>
        <SelectContent>
          {sortedOptions.map((opt) => (
            <SelectItem key={opt.optimisation_id} value={String(opt.optimisation_id)}>
              <div className="flex flex-col items-center gap-1 py-1">
                <div className="flex w-full items-center font-medium text-sm">
                  {!opt.constrained && (
                    <LuLockOpen className="mr-2 text-destructive" size={16} aria-label="Unconstrained" />
                  )}
                  <span className="flex">Optimisation #{opt.optimisation_id}</span>
                </div>
                <div className="flex items-center text-muted-foreground text-xs">
                  {opt.efficiency != null ? (opt.efficiency * 100).toFixed(1) : 'N/A'}% Efficiency
                  {opt.size_delta != null ? ` | ${opt.size_delta.toFixed(1)} cm Size Delta` : ''}
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export { OptimiserSelector };
